import { useLocalizedText } from '../../hooks/localizedText';
import { useLocale } from '../../hooks/locale';
import CopyToClipboard from '@amzn/awsui-components-react/polaris/copy-to-clipboard';
import Header from '@amzn/awsui-components-react/polaris/header';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import StatusIndicator from '@amzn/awsui-components-react/polaris/status-indicator';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CoordinatorShiftsPaginationPreferences,
  CoordinatorShiftsPaginationPreferencesProps,
} from './coordinatorEventShiftsTablePaginationPreference';
import Table from '@amzn/awsui-components-react/polaris/table';
import ButtonDropdown from '@amzn/awsui-components-react/polaris/button-dropdown';
import {
  AttendanceCompletion,
  DetailedShift,
  Event,
  EventDetails,
  EventStatus,
  GetShiftsByEventCommandOutput,
  LocationType,
  Source,
  Shift,
  ShiftTimingType,
  ShiftType,
} from '@amzn/red-velvet-api';
import {
  canEditEvent,
  generateCoordinatorTrackAttendanceUrl,
  generateShiftActionLink,
  generateShiftNumberOfPeopleAttended,
  generateShiftNumberOfSignedUpInfo,
  generateShiftStartDateInfo,
  generateShiftTotals,
  isShiftInProgress,
} from '../../utils/shiftUtils';
import { useTimezone } from '../../hooks/timezone';
import { SpaLink } from '../SpaLink';
import { useTableColumns, SHIFT_NAME_COLUMN_WIDTH_PX } from '../../hooks/tableColumns';
import { TruncatedCellText } from '../shifts/truncatedCellText';
import { ShiftTimeCell, resolveShiftDisplayZone } from '../shifts/shiftTimeCell';
import { resolveViewerZone } from '../../utils/dateFormatting';
import { useUser } from '../../hooks/user';
import { OpenEndedPagination, OpenEndedPaginationProps } from '../../hooks/openEndedPagination';
import { ManageEventButton } from '../shifts/actions';
import {
  EVENT_DATE_DISPLAY_WITH_MONTH_DAY_OPTIONS,
  formatEventHeaderDateRange,
  isUnlistedEvent,
} from '../../utils/eventUtils';
import {
  ADMIN_EVENT_PAGE_ACCESS,
  COORDINATOR_EVENT_SHIFTS_TABLE,
  EVENT_CREATION,
  SHIFT_WAITLIST,
  SIGN_UP_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE,
  SIGN_UP_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE,
  TRACK_ATTENDANCE_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE,
  TRACK_ATTENDANCE_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE,
} from '../../utils/constants';
import { useAllPagesRedVelvetQuery } from '../../hooks/allPagesRedVelvetQuery';
import { useFeatures, featureIsEnabled } from '../../hooks/features';
import { calculateShiftTime } from '../../hooks/shifts/shiftTime';
import { useAttendeeSessionStorage } from '../../hooks/attendance/attendeeSessionStorage';
import { useParticipantSessionStorage } from '../../hooks/attendance/participantSessionStorage';
import { useState } from 'react';
import { useDeleteEvent } from '../../hooks/events/deleteEvent';
import { DeleteEventModal } from './components/deleteEventModal';
import { useQueryClient } from '@tanstack/react-query';
import { publishKatalMetric } from '../katalAnalytics';

export const SHIFT_ATTENDANCE_STATUS_DISPLAY_MAP = {
  [AttendanceCompletion.UPCOMING]: {
    labelKey: 'coordinatorEventShiftsTable.attendanceStatus.upcoming',
    statusType: 'in-progress',
    color: 'grey',
  },
  [AttendanceCompletion.MISSING_ATTENDANCE]: {
    labelKey: 'coordinatorEventShiftsTable.attendanceStatus.missingAttendance',
    statusType: 'warning',
    color: 'red',
  },
  [AttendanceCompletion.COMPLETED]: {
    labelKey: 'coordinatorEventShiftsTable.attendanceStatus.completed',
    statusType: 'success',
    color: 'green',
  },
};

// A shift is visible in the coordinator table when it's not the phantom 'Waitlist' backing shift
// (matched by shiftType or trimmed name); DROP_IN stays — coordinators still manage walk-in shifts.
// Shared by the table body and the header counter so the rendered rows and the "(N)" count can't
// silently diverge.
const isCoordinatorVisibleShift = (shift: Shift): boolean =>
  shift.shiftType !== ShiftType.WAITLIST && shift.name?.trim() !== 'Waitlist';

/**
 * Coordinator kebab for an IN-PROGRESS shift (cce-7837, ShiftWaitlist-gated): Manage signups OR
 * Track attendance. Once the shift ends, the single Track attendance link takes over (see shiftActions).
 */
function CoordinatorInProgressActions({
  event,
  shift,
  urlFeatures,
}: {
  event: Event;
  shift: Shift;
  urlFeatures: string | null;
}) {
  const { t } = useLocalizedText(['activity', 'translation']);
  const navigate = useNavigate();
  const { resetParticipants } = useParticipantSessionStorage();
  const { resetAttendees } = useAttendeeSessionStorage();

  const manageSignupsUrl = `/manage-signups?eventId=${shift.eventId}&shiftId=${shift.shiftId}${urlFeatures ? '&features=' + urlFeatures : ''}`;
  // Route directly to the track-attendance flow. This kebab item is an explicit "Track attendance"
  // action, so it must not use generateShiftActionLink, which falls back to the sign-up view-event
  // page when attendanceCompletion is undefined/UPCOMING — the common case for an untracked
  // in-progress shift.
  const trackAttendanceUrl = generateCoordinatorTrackAttendanceUrl(
    shift.eventId,
    shift.shiftId,
    urlFeatures
  );

  return (
    <ButtonDropdown
      variant="inline-icon"
      expandToViewport
      ariaLabel={t('coordinatorEventShiftsTable.moreActions')}
      data-testid="coordinator.inProgressActions"
      items={[
        { id: 'manageSignups', text: t('coordinatorEventShiftsTable.manageSignupsAction') },
        { id: 'trackAttendance', text: t('coordinatorEventShiftsTable.trackAttendanceAction') },
      ]}
      onItemClick={({ detail }) => {
        if (detail.id === 'manageSignups') {
          navigate(manageSignupsUrl, { state: { event, shift } });
        } else if (detail.id === 'trackAttendance') {
          // Clear any stale in-progress tracking session before navigating (mirrors the single link).
          resetParticipants();
          resetAttendees();
          navigate(trackAttendanceUrl);
        }
      }}
    />
  );
}

const useShiftColumnMapForGivehubEvent = (
  selectedTimezone: string,
  urlFeatures: string | null,
  namespaces: string[] = ['translation'],
  fromEventDetailPage = false,
  event: Event,
  shifts: Shift[],
  isEventCreationEnabled = false
) => {
  const { t } = useLocalizedText(namespaces);
  const locale = useLocale();
  const { resetAttendees } = useAttendeeSessionStorage();
  const { resetParticipants } = useParticipantSessionStorage();
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const isShiftWaitlistEnabled = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);

  const shiftAttendanceTrackStatusColumn = {
    shiftAttendanceTrackStatus: (item: DetailedShift) => {
      const status = item.attendanceCompletion as AttendanceCompletion | undefined;

      if (
        item.shift.name !== 'Waitlist' &&
        (!status || !SHIFT_ATTENDANCE_STATUS_DISPLAY_MAP[status])
      ) {
        return (
          <StatusIndicator type="error">
            {t('coordinatorEventShiftsTable.unknownShiftAttendanceStatus')}
          </StatusIndicator>
        );
      }
      if (!status) {
        return <></>;
      }
      const { labelKey, statusType, color } = SHIFT_ATTENDANCE_STATUS_DISPLAY_MAP[status];
      return (
        <StatusIndicator
          type={statusType as 'success' | 'in-progress' | 'warning' | 'error'}
          colorOverride={color as 'green' | 'blue' | 'red' | 'grey'}
        >
          {t(labelKey)}
        </StatusIndicator>
      );
    },
  };

  const eventCreationColumns = {
    ...shiftAttendanceTrackStatusColumn,
    shiftLocation: (item: DetailedShift) => {
      const resolvedLocation =
        item.shift.useEventLocation !== false ? event.location : item.shift.location;
      const locType = resolvedLocation?.locationType;
      if (!locType) return '-';
      if (locType === LocationType.VIRTUAL) {
        return t('coordinatorEventShiftsTable.location.virtual');
      } else if (event.vcCreated) {
        return locType === LocationType.ON_SITE
          ? t('coordinatorEventShiftsTable.location.onCampus')
          : t('coordinatorEventShiftsTable.location.offCampus');
      } else if (resolvedLocation?.amazonBuilding) {
        return t('coordinatorEventShiftsTable.location.onCampus');
      } else {
        return t('coordinatorEventShiftsTable.location.inPerson');
      }
    },
  };

  return {
    shiftName: (item: DetailedShift) => <TruncatedCellText text={item.shift.name} />,
    ...(isEventCreationEnabled ? eventCreationColumns : {}),
    shiftStartDate: (item: DetailedShift) =>
      item.shift.shiftType === ShiftType.DROP_IN
        ? '-' // we should not display start date for Drop-in shift
        : generateShiftStartDateInfo(
            item.shift,
            // Render the date in the shift's display zone (the event's zone for an in-person shift)
            // so the Date and Time columns agree on the calendar day near a timezone day boundary.
            resolveShiftDisplayZone(event, item.shift, resolveViewerZone(selectedTimezone))
              .displayZone,
            locale,
            t('coordinatorEventShiftsTable.onGoing')
          ),
    time: (item: DetailedShift) =>
      item.shift.shiftType === ShiftType.DROP_IN ? (
        '-' // we should not display the time range for Drop-in shift
      ) : (
        <ShiftTimeCell
          event={event}
          shift={item.shift}
          emptyDisplay={t('coordinatorEventShiftsTable.onGoing')}
        />
      ),
    shiftSignupNumber: (item: DetailedShift) => {
      const { signupCount, numberOfGuests } = generateShiftNumberOfSignedUpInfo(
        item.shift,
        item.signups
      );
      // Unlimited shifts have no capacity to show against, so render the signup count alone.
      const isUnlimited = !item.shift.maxSignup;
      if (numberOfGuests > 0) {
        if (isUnlimited) {
          // count drives the i18next guest plural (one/other).
          return t('coordinatorEventShiftsTable.numberOfSignupsWithGuests', {
            signups: signupCount,
            count: numberOfGuests,
          });
        }
        // Capped with guests reads "{filled} of {max} spots ({guests} guest/guests)". The spots noun
        // pluralizes on the capacity and the guest noun on the guest count — two independent plurals
        // that cannot share one i18next count — so the guest clause is a separate pluralized suffix
        // (count = guest count) composed onto the capacity base (count = max). A guest occupies a
        // spot too, so filled counts volunteers + guests.
        const capacity = t('coordinatorEventShiftsTable.numberOfSignupsOfCapacity', {
          filled: signupCount + numberOfGuests,
          count: item.shift.maxSignup,
        });
        const guestSuffix = t('coordinatorEventShiftsTable.numberOfSignupsOfCapacityGuestSuffix', {
          count: numberOfGuests,
        });
        return `${capacity} ${guestSuffix}`;
      }
      return isUnlimited
        ? t('coordinatorEventShiftsTable.numberOfSignupsOnly', { count: signupCount })
        : t('coordinatorEventShiftsTable.numberOfSignupsOfCapacity', {
            filled: signupCount,
            count: item.shift.maxSignup,
          });
    },
    shiftAttendeesNumber: (item: DetailedShift) =>
      generateShiftNumberOfPeopleAttended(item.attendees),
    ...(isEventCreationEnabled ? {} : shiftAttendanceTrackStatusColumn),
    shiftActions: (item: DetailedShift) => {
      const isUpcoming = item.attendanceCompletion === AttendanceCompletion.UPCOMING;

      // For upcoming shifts, show "Manage signups" link
      if (isUpcoming) {
        return (
          <SpaLink
            href={`/manage-signups?eventId=${item.shift.eventId}&shiftId=${item.shift.shiftId}${urlFeatures ? '&features=' + urlFeatures : ''}`}
            state={{ event, shift: item.shift }}
          >
            {t('coordinatorEventShiftsTable.manageSignupsAction')}
          </SpaLink>
        );
      }

      // cce-7837 (ShiftWaitlist-gated): while a FLEXIBLE shift is IN PROGRESS (started, not ended)
      // offer a kebab so the coordinator can still Manage signups OR Track attendance. Once it ENDS,
      // the single Track attendance link below takes over. Scoped to FLEXIBLE to match the volunteer
      // side; gated on the ShiftWaitlist flag (not manage-signups).
      if (
        isShiftWaitlistEnabled &&
        item.shift.shiftTimingType === ShiftTimingType.FLEXIBLE &&
        !isUpcoming &&
        isShiftInProgress(item.shift)
      ) {
        return (
          <CoordinatorInProgressActions
            event={event}
            shift={item.shift}
            urlFeatures={urlFeatures}
          />
        );
      }

      return (
        <SpaLink
          href={generateShiftActionLink(
            item.attendanceCompletion,
            item.shift.shiftId,
            item.shift.eventId,
            urlFeatures,
            'signup'
          )}
          data-aci-analytics-name={
            item.attendanceCompletion
              ? isUpcoming
                ? fromEventDetailPage
                  ? SIGN_UP_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE
                  : SIGN_UP_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE
                : fromEventDetailPage
                  ? TRACK_ATTENDANCE_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE
                  : TRACK_ATTENDANCE_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE
              : ''
          }
          onFollow={() => {
            // A safeguard against these not being properly removed when navigating away from an in progress attendance tracking
            resetParticipants();
            resetAttendees();
          }}
        >
          {item.attendanceCompletion
            ? isUpcoming
              ? t('coordinatorEventShiftsTable.signupAction')
              : t('coordinatorEventShiftsTable.trackAttendanceAction')
            : ''}
        </SpaLink>
      );
    },
  };
};

const useShiftColumnMapForUnlistedEvent = (
  selectedTimezone: string,
  urlFeatures: string | null,
  namespaces: string[] = ['translation'],
  fromEventDetailPage = false,
  event?: Event,
  shifts?: Shift[]
) => {
  const { t } = useLocalizedText(namespaces);
  const locale = useLocale();

  return {
    shiftStartDate: (item: DetailedShift) =>
      generateShiftStartDateInfo(
        item.shift,
        selectedTimezone,
        locale,
        t('coordinatorEventShiftsTable.onGoing')
      ),
    shiftLength: (item: DetailedShift) =>
      `${calculateShiftTime(item.shift, event?.vcCreated)} ${t('coordinatorEventShiftsTable.shiftLength')}`,
    shiftAttendeesNumber: (item: DetailedShift) =>
      generateShiftNumberOfPeopleAttended(item.attendees),
    shiftActions: (item: DetailedShift) => (
      <SpaLink
        href={generateShiftActionLink(
          item.attendanceCompletion,
          item.shift.shiftId,
          item.shift.eventId,
          urlFeatures,
          'signup'
        )}
        data-aci-analytics-name={
          item.attendanceCompletion
            ? AttendanceCompletion.UPCOMING === item.attendanceCompletion
              ? fromEventDetailPage
                ? SIGN_UP_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE
                : SIGN_UP_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE
              : fromEventDetailPage
                ? TRACK_ATTENDANCE_ACTION_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_EVENT_DETAIL_PAGE
                : TRACK_ATTENDANCE_FROM_COORDINATOR_EVENT_SHIFTS_TABLE_FROM_COORDINATOR_ACTIVITY_PAGE
            : ''
        }
      >
        {item.attendanceCompletion
          ? AttendanceCompletion.UPCOMING === item.attendanceCompletion
            ? t('coordinatorEventShiftsTable.signupAction')
            : t('coordinatorEventShiftsTable.trackAttendanceAction')
          : ''}
      </SpaLink>
    ),
  };
};

export function CoordinatorEventShiftsTable({
  urlFeatures,
  paginationProps,
  preferencesProps,
  eventDetail,
  fromEventDetailPage = false,
}: {
  urlFeatures: string | null;
  paginationProps: OpenEndedPaginationProps;
  preferencesProps?: CoordinatorShiftsPaginationPreferencesProps;
  eventDetail: EventDetails;
  fromEventDetailPage?: boolean;
}) {
  const selectedTimezone = useTimezone();
  const [searchParams] = useSearchParams();
  const features = useFeatures();
  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);

  const visibleShifts = eventDetail.detailedShifts.filter((ds) =>
    isCoordinatorVisibleShift(ds.shift)
  );

  const shiftColumnMapForGivehubEvent = useShiftColumnMapForGivehubEvent(
    selectedTimezone,
    urlFeatures,
    ['activity', 'translation'],
    fromEventDetailPage,
    eventDetail.event,
    visibleShifts.map((ds) => ds.shift),
    isEventCreationEnabled
  );
  const shiftColumnMapForUnlistedEvent = useShiftColumnMapForUnlistedEvent(
    selectedTimezone,
    urlFeatures,
    ['activity', 'translation'],
    fromEventDetailPage,
    eventDetail.event,
    visibleShifts.map((ds) => ds.shift)
  );

  const { columnDefinitions } = useTableColumns(
    eventDetail.event.source === Source.GIVEHUB
      ? shiftColumnMapForGivehubEvent
      : shiftColumnMapForUnlistedEvent,
    'coordinatorEventShiftsTable',
    ['activity', 'translation'],
    {},
    { shiftName: SHIFT_NAME_COLUMN_WIDTH_PX }
  );
  return (
    <div>
      {visibleShifts.length !== 0 && (
        <Table
          header={
            <CoordinatorEventShiftsTableHeader
              eventDetails={eventDetail}
              urlFeatures={urlFeatures}
              selectedTimezone={selectedTimezone}
              fromEventDetailPage={fromEventDetailPage}
            />
          }
          columnDefinitions={columnDefinitions}
          resizableColumns
          stickyColumns={{ last: 1 }}
          pagination={<OpenEndedPagination {...paginationProps} />}
          preferences={<CoordinatorShiftsPaginationPreferences {...preferencesProps} />}
          items={visibleShifts}
          data-testid={'coordinator-activity-event-shifts-table'}
        />
      )}
    </div>
  );
}

interface CoordinatorEventShiftsTableHeaderProps {
  urlFeatures: string | null;
  selectedTimezone: string;
  eventDetails: EventDetails;
  fromEventDetailPage: boolean;
}

function CoordinatorEventShiftsTableHeader(props: CoordinatorEventShiftsTableHeaderProps) {
  const { t } = useLocalizedText(['activity', 'translation']);
  const { selectedTimezone, urlFeatures, eventDetails, fromEventDetailPage } = props;
  const user = useUser();
  const event: Event = eventDetails.event;
  const viewDetailsUrl = `/view-event?id=${event.eventId}${urlFeatures ? '&features=' + urlFeatures : ''}`;
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const { mutate: deleteEvent } = useDeleteEvent();
  const queryClient = useQueryClient();

  const shiftsQuery = useAllPagesRedVelvetQuery({
    redVelvetQueryKey: ['getShiftsByEvent', { eventId: event.eventId }],
    converter: (output: GetShiftsByEventCommandOutput) => ({
      items: output.shifts || [],
      next: output.page,
    }),
  });
  const shiftLists = shiftsQuery.data || [];
  // All coordinator-visible shifts across pages (shiftLists is drained) so the header "(N)" is the
  // true total — mirroring the volunteer table — not just the current server-paginated page.
  const visibleShiftCount = shiftLists.filter(isCoordinatorVisibleShift).length;

  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const isAdmin = featureIsEnabled(ADMIN_EVENT_PAGE_ACCESS, features, searchParams);

  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);
  const isShiftWaitlistEnabled = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);

  const canManage = canEditEvent(event, user, isAdmin);

  const hasCompletedShifts = eventDetails.detailedShifts.some(
    (ds) => ds.attendanceCompletion !== AttendanceCompletion.UPCOMING
  );

  const hasSignups = eventDetails.detailedShifts.some((ds) => (ds.signups?.length ?? 0) > 0);
  const totalAttendeeRecords = eventDetails.detailedShifts.reduce(
    (sum, ds) => sum + (ds.attendees?.length ?? 0),
    0
  );
  const hasAttendanceRecords = eventDetails.detailedShifts.some(
    (ds) => (ds.attendees?.length ?? 0) > 0
  );
  const editUnlistedEventUrl =
    shiftLists.length === 1
      ? `/track-attendance?eventId=${event.eventId}&shiftId=${shiftLists[0].shiftId}`
      : undefined;

  const copyForGrantsCloseoutUrl = isUnlistedEvent(event)
    ? editUnlistedEventUrl
      ? `https://www.volunteercenter.amazon.dev${editUnlistedEventUrl}`
      : undefined
    : isEventCreationEnabled
      ? event.status === EventStatus.DRAFT
        ? `https://www.volunteercenter.amazon.dev/create-event?draftId=${event.eventId}`
        : `https://www.volunteercenter.amazon.dev/edit-event?id=${event.eventId}`
      : `https://amazon.benevity.org/volunteer/${event.eventId}/edit`;

  return (
    <>
      <Header
        variant={fromEventDetailPage ? (isShiftWaitlistEnabled ? 'h3' : 'h2') : 'h1'}
        counter={
          fromEventDetailPage && isShiftWaitlistEnabled && !shiftsQuery.isLoading
            ? `(${visibleShiftCount})`
            : undefined
        }
        actions={
          canManage ? (
            <SpaceBetween direction="horizontal" size="xs">
              <ManageEventButton
                event={event}
                shifts={shiftLists}
                hasCompletedShifts={hasCompletedShifts}
                hasSignupsOrAttendances={hasSignups || hasAttendanceRecords}
                onDelete={() => setIsDeleteModalVisible(true)}
              />
              {copyForGrantsCloseoutUrl && (
                <CopyToClipboard
                  copyButtonText={t('coordinatorEventShiftsTable.actions.copyForGrantsCloseout')}
                  copyErrorText={t(
                    'coordinatorEventShiftsTable.actions.copyForGrantsCloseoutFailed'
                  )}
                  copySuccessText={t(
                    'coordinatorEventShiftsTable.actions.copyForGrantsCloseoutSuccess'
                  )}
                  data-aci-analytics-name="coordinatorActivity_copyEventUrl"
                  textToCopy={copyForGrantsCloseoutUrl}
                />
              )}
            </SpaceBetween>
          ) : undefined
        }
        description={
          fromEventDetailPage ? null : (
            <CoordinatorEventShiftsTableHeaderDescription
              selectedTimezone={selectedTimezone}
              urlFeatures={urlFeatures}
              eventDetails={eventDetails}
              shiftLists={shiftLists}
            />
          )
        }
      >
        {fromEventDetailPage && t('coordinatorEventShiftsTable.tableTitle.shift')}
        {!fromEventDetailPage &&
          (event.source === Source.GIVEHUB ? (
            <SpaLink
              href={viewDetailsUrl}
              data-aci-analytics-name={COORDINATOR_EVENT_SHIFTS_TABLE + '_eventTitleLink'}
            >
              <h2 style={{ margin: 0 }}>{event.title}</h2>
            </SpaLink>
          ) : (
            <h4 style={{ margin: 0 }}>{event.title}</h4>
          ))}
      </Header>
      <DeleteEventModal
        visible={isDeleteModalVisible}
        loading={isDeleteLoading}
        eventTitle={event.title ?? ''}
        attendeeCount={totalAttendeeRecords}
        onConfirm={() => {
          setIsDeleteLoading(true);
          publishKatalMetric('createEvent_deleteEvent', event.eventId);
          deleteEvent(
            { eventId: event.eventId },
            {
              onSuccess: () => {
                setIsDeleteLoading(false);
                setIsDeleteModalVisible(false);
                queryClient.invalidateQueries({
                  queryKey: ['RedVelvetApi', 'getEventDetailsManagedByUser'],
                });
                queryClient.invalidateQueries({
                  queryKey: ['RedVelvetApi', 'getUnpublishedEventsManagedByUser'],
                });
              },
              onError: () => {
                setIsDeleteLoading(false);
                setIsDeleteModalVisible(false);
              },
            }
          );
        }}
        onDismiss={() => setIsDeleteModalVisible(false)}
      />
    </>
  );
}

interface CoordinatorEventShiftsTableHeaderDescriptionProps {
  urlFeatures: string | null;
  selectedTimezone: string;
  eventDetails: EventDetails;
  shiftLists: Shift[];
}

function CoordinatorEventShiftsTableHeaderDescription(
  props: CoordinatorEventShiftsTableHeaderDescriptionProps
) {
  const { t } = useLocalizedText(['activity', 'translation']);
  const locale = useLocale();
  const { selectedTimezone, urlFeatures, eventDetails, shiftLists } = props;
  const event: Event = eventDetails.event;

  const viewDetailsUrl = `/view-event?id=${event.eventId}${urlFeatures ? '&features=' + urlFeatures : ''}`;
  const { startDay, endDay } = formatEventHeaderDateRange(
    event,
    resolveViewerZone(selectedTimezone),
    locale,
    EVENT_DATE_DISPLAY_WITH_MONTH_DAY_OPTIONS
  );

  const getDisplayLocation = (event: Event): string => {
    if (event.location.locationType === LocationType.VIRTUAL) {
      return t('coordinatorEventShiftsTable.eventDisplay.virtualLocation');
    } else if (event.location.city && event.location.province && event.location.country) {
      return t('coordinatorEventShiftsTable.eventDisplay.physicalLocation', {
        city: event.location.city,
        state: event.location.province,
        country: event.location.country,
      });
    } else if (event.location.amazonBuilding) {
      return event.location.amazonBuilding;
    } else if (event.location.address_string) {
      return event.location.address_string;
    } else return '';
  };

  const displayedLocation = getDisplayLocation(event);
  // Count spots/shifts over the same coordinator-visible shifts the table renders (and the header
  // "(N)" counter uses), so the phantom 'Waitlist' backing shift never inflates the totals.
  const { totalSpots, totalShifts } = generateShiftTotals(
    shiftLists.filter(isCoordinatorVisibleShift)
  );
  const hasUnlimitedSpots = totalSpots === Infinity;
  return (
    <span>
      <span>
        <small className={'coordinator-table-event-header-none-description'}>
          {startDay}
          {endDay ? ` - ${endDay}` : ''} | {displayedLocation}
        </small>
        <small className={'coordinator-table-event-header-none-description'}>
          {hasUnlimitedSpots
            ? t('coordinatorEventShiftsTable.eventDisplay.unlimited')
            : t('coordinatorEventShiftsTable.eventDisplay.totalSpots', {
                count: totalSpots,
              })}{' '}
          {t('coordinatorEventShiftsTable.eventDisplay.totalShifts', {
            count: totalShifts,
          })}
          {eventDetails.waitlistCount !== undefined && eventDetails.waitlistCount > 0 && (
            <>
              {' | '}
              <SpaLink
                href={`/manage-waitlist?eventId=${event.eventId}${urlFeatures ? '&features=' + urlFeatures : ''}`}
                fontSize="inherit"
                data-aci-analytics-name={COORDINATOR_EVENT_SHIFTS_TABLE + '_waitlistCountLink'}
              >
                {t('coordinatorEventShiftsTable.eventDisplay.inWaitlist', {
                  count: eventDetails.waitlistCount,
                })}
              </SpaLink>
            </>
          )}
        </small>
      </span>
      {event.communityOrganizations?.[0]?.name && (
        <small className={'coordinator-table-event-header-organization'}>
          {t('coordinatorEventShiftsTable.eventDisplay.organization', {
            organization: event.communityOrganizations[0].name,
          })}
        </small>
      )}
      <span className={'coordinator-table-event-header-description'}>
        {event.shortDescription && event.shortDescription.length > 180
          ? `${event.shortDescription.slice(0, 180)}...`
          : event.shortDescription}
        {'\t'}
        {event.source === Source.GIVEHUB && (
          <SpaLink
            href={viewDetailsUrl}
            data-aci-analytics-name={COORDINATOR_EVENT_SHIFTS_TABLE + '_readMoreLink'}
          >
            {t('coordinatorEventShiftsTable.eventDisplay.readMore')}
          </SpaLink>
        )}
      </span>
    </span>
  );
}
